/**
 * @fileoverview Утилиты для обработки изображений, сжатия и создания водяных знаков.
 * Включает функции конвертации форматов, оптимизации размера файлов и наложения
 * метаданных на фотографии для Telegram Web App.
 * 
 * @since 1.0.0
 */

import { logger } from "@sentry/react";
import { getAddressTime } from "../api/api";
import { OperationData } from "../../types/OperationData";
import { GeolocationType } from "./hooks/useGeolocation";

/** Максимальный размер изображения в байтах (1 МБ) для оптимизации */
const ONE_MB_IN_BYTES = 1_048_576

/**
 * Поддерживаемые MIME типы изображений.
 * 
 * @since 1.0.0
 */
enum ImageMime {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  WEBP = 'image/webp',
}

/**
 * Тип данных для создания файлов (возможно legacy, требует рефакторинга).
 * 
 * @deprecated Тип используется ограниченно, возможно требует пересмотра
 * @since 1.0.0
 */
export type ToFileType = {
  /** Тип операции с файлом */
  type: 'create' | 'finish' | undefined
  /** Государственный регистрационный знак автомобиля */
  grz?: string | null
  /** Новый статус операции */
  newStatus: string | undefined
  /** Адрес местоположения */
  address: string
};

/**
 * Параметры для обработки изображения.
 * 
 * @since 1.0.0
 */
type ProcessingOptions = {
  /** Base64 представление изображения */
  base64: string
};

/**
 * Конвертирует файл изображения в base64 строку.
 * 
 * @param imageFile - Файл изображения для конвертации
 * 
 * @returns Promise с base64 строкой или undefined при ошибке
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const file = new File([blob], "image.jpg", { type: "image/jpeg" });
 * const base64String = await getDecodedImage(file);
 * ```
 */
export const getDecodedImage = async (imageFile: File) => {
  const result = await toBase64(imageFile)

  if (typeof result === 'string') {
    return result
  }

  return
}

/**
 * Конвертирует файл в base64 с помощью FileReader API.
 * 
 * @param file - Файл для конвертации
 * 
 * @returns Promise с результатом чтения (строка, ArrayBuffer или null)
 * 
 * @throws {Error} При ошибках чтения файла
 * 
 * @since 1.0.0
 */
const toBase64 = (file: File) =>
  new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const reader = new FileReader()

    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })

/**
 * Создаёт объект File из data URL с обработкой и сжатием.
 * 
 * @param dataUrl - Data URL изображения (обычно base64)
 * 
 * @returns Promise с обработанным File объектом
 * 
 * @remarks
 * Автоматически применяет сжатие для оптимизации размера.
 * Генерирует уникальное имя файла на основе временной метки.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const dataUrl = "data:image/jpeg;base64,...";
 * const file = await getImageFile(dataUrl);
 * console.log(file.name); // "временная_метка"
 * ```
 */
export const getImageFile = async (dataUrl: string): Promise<File> => {
  const blob = await processImage({ base64: dataUrl })

  return new File([blob], `${Date.now()}`, { type: 'image/png' })
}

/**
 * Обрабатывает изображение: конвертирует в canvas и сжимает.
 * 
 * @param options - Параметры обработки с обязательным полем base64
 * 
 * @returns Promise с сжатым Blob объектом
 * 
 * @remarks
 * Обработка проходит в два этапа:
 * 1. Конвертация base64 в Canvas элемент
 * 2. Сжатие с максимальным качеством
 * 
 * @since 1.0.0
 * 
 * @see {@link imgToCanvas} для конвертации в canvas
 * @see {@link compressImg} для сжатия
 */
export const processImage = async ({ base64 }: ProcessingOptions) => {
  const blob = await imgToCanvas(base64).then((canvas) =>
    compressImg(canvas, true)
  )

  return blob
}

/**
 * Сжимает изображение на canvas до размера менее 1 МБ.
 * 
 * @param canvas - HTML Canvas элемент с изображением
 * @param maxQuality - Если true, сохраняет максимальное качество
 * 
 * @returns Promise с сжатым Blob объектом
 * 
 * @remarks
 * Алгоритм сжатия:
 * - Начинает с качества 100% (quality = 1)
 * - Постепенно снижает на 5% до достижения 1 МБ
 * - Минимальное качество: 10%
 * - Использует JPEG формат для оптимального сжатия
 * 
 * @since 1.0.0
 */
const compressImg = async (canvas: HTMLCanvasElement, maxQuality?: boolean) => {
  const context = canvas.getContext('2d')
  let quality = 1
  //@ts-ignore
  let blob: Blob = await canvasToBlob(canvas, quality)

  if (!maxQuality) {
    while (blob.size > ONE_MB_IN_BYTES && quality > 0.1) {
      quality = quality - 0.05
      await createImageBitmap(blob).then((bit) =>
        context!.drawImage(bit, 0, 0)
      )
      //@ts-ignore
      blob = await canvasToBlob(canvas, quality)
    }
  }

  return blob
}

/**
 * Конвертирует Canvas элемент в Blob с указанным качеством.
 * 
 * @param canvas - HTML Canvas элемент для конвертации
 * @param quality - Качество сжатия от 0 до 1
 * 
 * @returns Promise с Blob объектом в JPEG формате
 * 
 * @since 1.0.0
 */
const canvasToBlob = async (canvas: HTMLCanvasElement, quality: number) => {
  return await new Promise((resolve) =>
    canvas.toBlob(resolve, ImageMime.JPEG, quality)
  )
}

/**
 * Конвертирует изображение из URL в Canvas элемент.
 * 
 * @param url - URL или data URL изображения
 * 
 * @returns Promise с HTML Canvas элементом, содержащим изображение
 * 
 * @remarks
 * Автоматически устанавливает crossorigin="anonymous" для работы с CORS.
 * Сохраняет оригинальные размеры изображения.
 * Ожидает полной загрузки изображения перед отрисовкой.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const dataUrl = "data:image/jpeg;base64,...";
 * const canvas = await imgToCanvas(dataUrl);
 * console.log(canvas.width, canvas.height);
 * ```
 */
const imgToCanvas = async (url: string) => {
  const img = document.createElement('img')

  img.src = url
  img.setAttribute('crossorigin', 'anonymous')
  await new Promise((resolve) => (img.onload = resolve))
  const canvas = document.createElement('canvas')

  canvas.width = img.width
  canvas.height = img.height
  // @ts-ignore
  canvas.getContext('2d').drawImage(img, 0, 0)

  return canvas
}


/**
 * Инициирует скачивание файла через браузер.
 * 
 * @param file - Файл для скачивания
 * 
 * @remarks
 * Создаёт временную ссылку для скачивания и автоматически очищает DOM.
 * Использует стандартный механизм браузера для сохранения файлов.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
 * downloadFile(file); // Начнётся скачивание
 * ```
 */
export function downloadFile(file: File) {
  const link = document.createElement('a')
  link.href = URL.createObjectURL(file)
  link.download = file.name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Накладывает текстовые строки на изображение в качестве водяного знака.
 * 
 * @param image - HTML Image элемент с исходным изображением
 * @param textLines - Массив строк для отображения на изображении
 * 
 * @returns Data URL с обработанным изображением или null при ошибке
 * 
 * @remarks
 * **Стиль водяного знака:**
 * - Шрифт: Arial 20px
 * - Цвет текста: белый
 * - Обводка: чёрная, ширина 3px
 * - Позиция: правый верхний угол с отступом 20px
 * - Межстрочный интервал: 20px
 * 
 * **Обработка:**
 * - Каждая строка выравнивается по правому краю
 * - Сохраняет оригинальные размеры изображения
 * - Возвращает JPEG формат для оптимального размера
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const img = new Image();
 * img.src = "data:image/jpeg;base64,...";
 * const watermarkText = ["Время: 26.07.2025", "Москва", "55.123456 37.654321"];
 * const result = draw(img, watermarkText);
 * ```
 */
export const draw = (image: HTMLImageElement, textLines: string[]): string | null => {
  const canvas = document.createElement('canvas');
  const lineHeight = 20;

  canvas.width = image.width;
  canvas.height = image.height;
  const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

  if (ctx) {
      ctx.drawImage(image, 0, 0);

      const startY = 20;
      const drawStroked = (text: string, x: number, y: number) => {
          ctx.font = "20px Arial"
          ctx.strokeStyle = 'black';
          ctx.lineWidth = 3;
          ctx.miterLimit = 2;
          ctx.strokeText(text, x, y);
          ctx.fillStyle = 'white';
          ctx.fillText(text, x, y);
      }

      textLines.forEach((line, index) => {
          const textWidth = ctx.measureText(line).width;
          const startX = canvas.width - textWidth - 20;
          drawStroked(line, startX, startY + index * lineHeight);
      });

      const watermarkedImageSrc = canvas.toDataURL('image/jpeg');
      return watermarkedImageSrc;
  }
  return null;
};

/**
 * Главная функция добавления водяного знака с полными метаданными операции.
 * 
 * Создает водяной знак содержащий:
 * - Государственный регистрационный знак (ГРЗ)
 * - Время и дату в московском часовом поясе
 * - Имя исполнителя
 * - Название локации операции
 * - Точные GPS координаты (6 знаков после запятой)
 * - Человекочитаемый адрес через DaData API
 * 
 * @param imageSrc - Data URL исходного изображения
 * @param operationData - Данные операции (ГРЗ, исполнитель, локация)
 * @param location - GPS координаты для водяного знака
 * 
 * @returns Promise с data URL обработанного изображения с водяным знаком
 * 
 * @throws {Error} При ошибках загрузки изображения или API запросов
 * @throws {Error} При сбоях Canvas обработки
 * 
 * @remarks
 * **Бизнес-логика водяного знака:**
 * 1. Получает адрес и время через getAddressTime API
 * 2. Формирует массив текстовых строк с метаданными
 * 3. Использует fallback значения при отсутствии данных
 * 4. Применяет координаты с точностью до 6 знаков (≈1 метр)
 * 
 * **Обработка ошибок:**
 * - Логирует все ошибки через Sentry
 * - Graceful degradation для недоступных данных
 * - Детальные сообщения об ошибках для отладки
 * 
 * **Производительность:**
 * - Асинхронная загрузка изображений
 * - Оптимизированный Canvas rendering
 * - Минимальное использование памяти
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * const imageSrc = "data:image/jpeg;base64,...";
 * const operation = {
 *   grz: "А123БВ777",
 *   doer_name: "Иван Петров",
 *   location: { name: "Сервис на Ленинском" }
 * };
 * const coords = { latitude: 55.7558, longitude: 37.6176 };
 * 
 * try {
 *   const watermarkedImage = await addWatermark(imageSrc, operation, coords);
 *   // watermarkedImage содержит обработанное изображение
 * } catch (error) {
 *   console.error("Ошибка водяного знака:", error);
 * }
 * ```
 * 
 * @see {@link draw} для логики отрисовки текста
 * @see {@link getAddressTime} для получения адреса и времени
 */
export const addWatermark = async (imageSrc: string, operationData: OperationData, location: GeolocationType): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      const lat = location?.latitude || 0;
      const lng = location?.longitude || 0;
      const addressTimeResponse = await getAddressTime(lat, lng);

      const textLines = [
        '', // empty line needed for the first line
        `${operationData?.grz || 'grz'}`,
        `${addressTimeResponse.time}`,
        `${operationData?.doer_name || 'doer_name'}`,
        `${operationData?.location?.name || 'location_name'}`,
        `${lat.toFixed(6)} ${lng.toFixed(6)}`,
        addressTimeResponse.address.split(',').map((item) => item.trim()),
      ].flat();

      const image = new Image();
      
      image.onload = () => {
        try {
          const watermarkedImageSrc = draw(image, textLines);
          if (watermarkedImageSrc) {
            resolve(watermarkedImageSrc);
          } else {
            reject(new Error('Ошибка создания водяного знака'));
          }
        } catch (error) {
          reject(error);
        }
      };
      
      image.onerror = () => {
        reject(new Error('Ошибка загрузки изображения'));
      };
      
      image.src = imageSrc;
    } catch (error) {
      logger.error('Ошибка обработки изображения:', { error });
      reject(error);
    }
  });
};

